import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BadgeCheck,
  Building2,
  Camera,
  CreditCard,
  ExternalLink,
  FileText,
  Mail,
  MapPin,
  PencilLine,
  ShieldAlert,
  UserRound,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Notification, {
  showToast,
} from "../../../components/common/notification/Notification";
import "./profile.css";

const Profile = () => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const userId = loggedInUser?.user?.id;
  const token = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [userDetails, setUserDetails] = useState(null);
  const [assetUrl, setAssetUrl] = useState("");
  const [profilePicture, setProfilePicture] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchProfileRequest = async () => {
      if (!baseUrl || !token || !userId) {
        return null;
      }

      return axios.get(`${baseUrl}/admin/user/id/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    };

    const fetchProfile = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const responseData = await fetchProfileRequest();
        const nextUser = responseData?.data?.user ?? null;
        const nextAssetUrl = responseData?.data?.assetUrl ?? "";
        const nextProfilePhoto = nextUser?.photo
          ? `${nextAssetUrl}${nextUser.photo}`
          : "";

        if (!isMounted) {
          return;
        }

        setUserDetails(nextUser);
        setAssetUrl(nextAssetUrl);
        setProfilePicture(nextProfilePhoto);
      } catch (error) {
        console.error("Error fetching profile:", error);
        if (isMounted) {
          showToast("Unable to load your profile right now.", {
            type: "error",
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchSubscriptionDetails = async () => {
      if (!userId || !baseUrl) {
        setIsSubscriptionLoading(false);
        return;
      }

      setIsSubscriptionLoading(true);

      try {
        const response = await axios.get(
          `${baseUrl}/payments/subscriptions/user/${userId}`
        );

        if (isMounted) {
          setSubscriptionDetails(response?.data ?? null);
        }
      } catch (error) {
        console.error("Error fetching subscription details:", error);
        if (isMounted) {
          setSubscriptionDetails(null);
        }
      } finally {
        if (isMounted) {
          setIsSubscriptionLoading(false);
        }
      }
    };

    fetchProfile();
    fetchSubscriptionDetails();

    return () => {
      isMounted = false;
    };
  }, [baseUrl, token, userId]);

  useEffect(() => {
    if (!selectedFile) {
      return undefined;
    }

    const previewUrl = URL.createObjectURL(selectedFile);
    setProfilePicture(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [selectedFile]);

  const handleProfilePictureChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setSelectedFile(file);
  };

  const handleProfilePictureUpload = async () => {
    if (!selectedFile) {
      showToast("Please select a profile picture.", { type: "error" });
      return;
    }

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("file", selectedFile);

    try {
      setUploading(true);

      const response = await axios.put(
        `${baseUrl}/admin/changePicture`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response?.data?.statusType === "SUCCESS") {
        showToast("Profile picture uploaded successfully.", {
          type: "success",
        });
        setSelectedFile(null);

        const responseData = await axios.get(
          `${baseUrl}/admin/user/id/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const nextUser = responseData?.data?.user ?? null;
        const nextAssetUrl = responseData?.data?.assetUrl ?? "";
        const nextProfilePhoto = nextUser?.photo
          ? `${nextAssetUrl}${nextUser.photo}`
          : "";

        setUserDetails(nextUser);
        setAssetUrl(nextAssetUrl);
        setProfilePicture(nextProfilePhoto);
      } else {
        showToast("Something went wrong while uploading the picture.", {
          type: "error",
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Upload failed. Please try again.", { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const fullName = `${userDetails?.first_name || ""} ${
    userDetails?.last_name || ""
  }`.trim();
  const displayName = fullName || "Customer";
  const userTypeLabel =
    userDetails?.userType?.name?.replaceAll("_", " ") ?? "Customer";
  const emailAddress = userDetails?.email_address || "Not provided";
  const whatsappNumber = userDetails?.whatsapp_mobile_number || "Not provided";
  const cityName = userDetails?.cities?.[0]?.name || "Not selected";
  const stateName = userDetails?.cities?.[0]?.state?.name || "Not selected";
  const documents = userDetails?.documents ?? [];
  const isSubscriptionActive = Boolean(subscriptionDetails?.subscriptionPlanActive);

  const profileCompletion = useMemo(() => {
    const checkpoints = [
      Boolean(userDetails?.email_address),
      Boolean(userDetails?.whatsapp_mobile_number),
      Boolean(userDetails?.cities?.[0]?.name),
      documents.length > 0,
      Boolean(profilePicture),
    ];

    const completedCount = checkpoints.filter(Boolean).length;
    return Math.round((completedCount / checkpoints.length) * 100);
  }, [documents.length, profilePicture, userDetails]);

  const initials = getInitials(displayName);

  return (
    <>
      <Notification />

      <div className="cupf">
        <div className="cupf__section">
          <div className="cupf__actions" aria-label="Profile quick actions">
            <Link to="/customer/profile/edit" className="cupf__action cupf__action--primary">
              <PencilLine size={16} strokeWidth={2} />
              <span>Edit Profile</span>
            </Link>
            <Link to="/customer/profile/documents" className="cupf__action cupf__action--ghost">
              <FileText size={16} strokeWidth={2} />
              <span>Documents</span>
            </Link>
            <Link to="/customer/profile/payment" className="cupf__action cupf__action--dark">
              <CreditCard size={16} strokeWidth={2} />
              <span>Payments</span>
            </Link>
          </div>

          <div className="cupf__grid">
            <article className="cupf__card cupf__card--profile">
              {isLoading ? (
                <div className="cupf__loading">
                  <div className="cupf__loading-avatar" />
                  <div className="cupf__loading-line cupf__loading-line--title" />
                  <div className="cupf__loading-line" />
                  <div className="cupf__loading-line cupf__loading-line--short" />
                </div>
              ) : (
                <>
                  <div className="cupf__profile-head">
                    <div className="cupf__avatar-wrap">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          alt={`${displayName} profile`}
                          className="cupf__avatar-image"
                        />
                      ) : (
                        <div className="cupf__avatar-fallback" aria-hidden="true">
                          {initials}
                        </div>
                      )}

                      <label className="cupf__avatar-trigger" htmlFor="cupf-profile-upload">
                        <Camera size={14} strokeWidth={2.2} />
                        <span>Change</span>
                      </label>
                      <input
                        id="cupf-profile-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                        className="cupf__file-input"
                      />
                    </div>

                    <div className="cupf__profile-copy">
                      <p className="cupf__eyebrow">Account Overview</p>
                      <h2 className="cupf__profile-name">{displayName}</h2>
                      <p className="cupf__profile-role">{userTypeLabel}</p>

                      <div className="cupf__verification">
                        {userDetails?.email_verified ? (
                          <span className="cupf__pill cupf__pill--verified">
                            <BadgeCheck size={14} strokeWidth={2} />
                            <span>Email verified</span>
                          </span>
                        ) : (
                          <span className="cupf__pill cupf__pill--warning">
                            <ShieldAlert size={14} strokeWidth={2} />
                            <span>Email not verified</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedFile ? (
                    <div className="cupf__upload-banner">
                      <div>
                        <p className="cupf__upload-title">Ready to update your photo</p>
                        <p className="cupf__upload-text">
                          Your new picture is selected and ready to upload.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleProfilePictureUpload}
                        disabled={uploading}
                        className="cupf__button cupf__button--primary"
                      >
                        {uploading ? "Uploading..." : "Upload Picture"}
                      </button>
                    </div>
                  ) : (
                    <div className="cupf__upload-hint">
                      A polished profile builds trust faster with owners, brokers,
                      and support teams.
                    </div>
                  )}

                  <div className="cupf__stats">
                    <StatCard label="Profile completion" value={`${profileCompletion}%`} />
                    <StatCard label="Documents" value={`${documents.length}`} />
                    <StatCard
                      label="Plan"
                      value={isSubscriptionActive ? "Active" : "Inactive"}
                    />
                  </div>
                </>
              )}
            </article>

            <article className="cupf__card cupf__card--summary">
              <div className="cupf__section-heading">
                <p className="cupf__eyebrow">Quick Snapshot</p>
                <h3 className="cupf__section-title">Everything important at a glance</h3>
              </div>

              <div className="cupf__summary-list">
                <InfoRow
                  icon={<Mail size={18} strokeWidth={2} />}
                  label="Email address"
                  value={emailAddress}
                />
                <InfoRow
                  icon={<FaWhatsapp size={18} />}
                  label="WhatsApp"
                  value={whatsappNumber}
                  accent="whatsapp"
                />
                <InfoRow
                  icon={<MapPin size={18} strokeWidth={2} />}
                  label="City"
                  value={cityName}
                />
                <InfoRow
                  icon={<Building2 size={18} strokeWidth={2} />}
                  label="State"
                  value={stateName}
                />
                <InfoRow
                  icon={<UserRound size={18} strokeWidth={2} />}
                  label="Account type"
                  value={userTypeLabel}
                />
              </div>
            </article>
          </div>
        </div>

        <div className="cupf__section cupf__section--alt">
          <div className="cupf__content-grid">
            <article className="cupf__card">
              <div className="cupf__section-heading">
                <p className="cupf__eyebrow">Personal Details</p>
                <h3 className="cupf__section-title">Clean, readable account information</h3>
              </div>

              <div className="cupf__details-grid">
                <DetailItem label="First name" value={userDetails?.first_name} />
                <DetailItem label="Last name" value={userDetails?.last_name} />
                <DetailItem label="Email" value={userDetails?.email_address} />
                <DetailItem
                  label="Email status"
                  value={userDetails?.email_verified ? "Verified" : "Pending verification"}
                />
                <DetailItem
                  label="WhatsApp number"
                  value={userDetails?.whatsapp_mobile_number}
                />
                <DetailItem label="Primary city" value={cityName} />
                <DetailItem label="Primary state" value={stateName} />
                <DetailItem label="Uploaded documents" value={`${documents.length}`} />
              </div>
            </article>

            <article className="cupf__card">
              <div className="cupf__section-heading">
                <p className="cupf__eyebrow">Subscription</p>
                <h3 className="cupf__section-title">Plan and billing visibility</h3>
              </div>

              {isSubscriptionLoading ? (
                <div className="cupf__empty">Loading subscription details...</div>
              ) : isSubscriptionActive ? (
                <div className="cupf__subscription">
                  <div className="cupf__subscription-top">
                    <div>
                      <p className="cupf__subscription-label">Current plan</p>
                      <h4 className="cupf__subscription-name">
                        {subscriptionDetails?.subscriptionPlan?.name || "Active plan"}
                      </h4>
                    </div>
                    <span className="cupf__pill cupf__pill--verified">Active</span>
                  </div>

                  <div className="cupf__details-grid">
                    <DetailItem
                      label="Remaining days"
                      value={subscriptionDetails?.effectiveSubscriptionDays}
                    />
                    <DetailItem
                      label="Duration"
                      value={`${subscriptionDetails?.durationInDays || 0} days`}
                    />
                    <DetailItem
                      label="Price"
                      value={formatCurrency(subscriptionDetails?.subscriptionPlan?.price)}
                    />
                    <DetailItem
                      label="GST"
                      value={formatCurrency(subscriptionDetails?.subscriptionPlan?.gst)}
                    />
                    <DetailItem
                      label="Total"
                      value={formatCurrency(
                        subscriptionDetails?.subscriptionPlan?.total_price
                      )}
                    />
                  </div>
                </div>
              ) : (
                <EmptyState
                  title="No active subscription yet"
                  description="Your profile is ready, but you do not have an active subscription plan connected to this account."
                />
              )}
            </article>
          </div>
        </div>

        <div className="cupf__section">
          <article className="cupf__card">
            <div className="cupf__section-heading cupf__section-heading--split">
              <div>
                <p className="cupf__eyebrow">Documents</p>
                <h3 className="cupf__section-title">Uploaded files with better scanability</h3>
              </div>
              <Link to="/customer/profile/documents" className="cupf__text-link">
                <span>Manage documents</span>
                <ExternalLink size={15} strokeWidth={2} />
              </Link>
            </div>

            {documents.length > 0 ? (
              <div className="cupf__documents-grid">
                {documents.map((doc, index) => (
                  <article key={`${doc.document_url}-${index}`} className="cupf__document-card">
                    <div className="cupf__document-preview">
                      <img
                        src={`${assetUrl}${doc.document_url}`}
                        alt={doc.name || "Document preview"}
                        className="cupf__document-image"
                      />
                    </div>
                    <div className="cupf__document-copy">
                      <p className="cupf__document-name">{doc.name || "Uploaded document"}</p>
                      <a
                        href={`${assetUrl}${doc.document_url}`}
                        target="_blank"
                        rel="noreferrer"
                        className="cupf__document-link"
                      >
                        <span>Open file</span>
                        <ExternalLink size={14} strokeWidth={2} />
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No documents uploaded"
                description="Add your supporting files to speed up verification and keep your profile complete."
              />
            )}
          </article>
        </div>
      </div>
    </>
  );
};

const StatCard = ({ label, value }) => (
  <div className="cupf__stat-card">
    <p className="cupf__stat-value">{value}</p>
    <p className="cupf__stat-label">{label}</p>
  </div>
);

const InfoRow = ({ icon, label, value, accent = "primary" }) => (
  <div className="cupf__info-row">
    <div className={`cupf__info-icon cupf__info-icon--${accent}`}>{icon}</div>
    <div className="cupf__info-copy">
      <p className="cupf__info-label">{label}</p>
      <p className="cupf__info-value">{value || "Not provided"}</p>
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="cupf__detail-item">
    <p className="cupf__detail-label">{label}</p>
    <p className="cupf__detail-value">{value || "Not provided"}</p>
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="cupf__empty">
    <p className="cupf__empty-title">{title}</p>
    <p className="cupf__empty-text">{description}</p>
  </div>
);

const getInitials = (name) => {
  const trimmedName = name?.trim();

  if (!trimmedName) {
    return "CU";
  }

  const segments = trimmedName.split(/\s+/).filter(Boolean);

  if (segments.length === 1) {
    return segments[0].slice(0, 2).toUpperCase();
  }

  return `${segments[0][0]}${segments[1][0]}`.toUpperCase();
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return "Not available";
  }

  const numericAmount = Number(amount);

  if (Number.isNaN(numericAmount)) {
    return `Rs. ${amount}`;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numericAmount);
};

export default Profile;
